import { HttpStatusCode } from '@thesavingsgroup/enums/HttpStatusCode';
import { StateCreator } from 'zustand';

import { AuthStatus } from '../userStore/enums/AuthStatus';
import { ApiState } from './types/ApiState';
import { ApiStateDependencies } from './types/ApiStateDependencies';
import { HttpClientInstance } from './types/HttpClientInstance';

export const apiStore =
  ({
    config,
    httpClient,
    useSignOutStore,
    useUserStore,
  }: ApiStateDependencies): StateCreator<ApiState> =>
  (set, get) => ({
    isLoading: false,

    featureApi: (): HttpClientInstance => {
      const featureHttpRequest = httpClient.create({
        headers: {
          Authorization: config.unleash.clientKey,
          'Content-Type': 'application/json',
        },
        timeout: 15_000,
      });

      featureHttpRequest.interceptors.response.use((response): any => {
        return response.data;
      });

      return featureHttpRequest;
    },
    handleUnauthorized: (error: any): void => {
      const statusCode = error?.response?.status;

      if (statusCode !== HttpStatusCode.UNAUTHORIZED) {
        return;
      }

      const { authStatus } = useUserStore.getState();

      if (authStatus !== AuthStatus.AUTHENTICATED) {
        return;
      }

      const { signOut } = useSignOutStore.getState();

      signOut({
        withAuthRedirectPath: window.location.pathname,
      });
    },
    httpRequest: (): HttpClientInstance => httpClient.create(),
    portalApi: (): HttpClientInstance => {
      const { handleUnauthorized } = get();

      const portalHttpRequest = httpClient.create({
        baseURL: new URL(config.api.path, config.api.baseUrl).href,
        headers: {
          'x-tsg-current-hash': import.meta.env.VITE_APP_COMMIT_HASH,
          'x-tsg-current-url': window.location.href,
          'x-tsg-current-version': import.meta.env.VITE_APP_VERSION,
        },
        withCredentials: true,
      });

      portalHttpRequest.interceptors.request.use((requestConfig): any => {
        set({ isLoading: true });

        return requestConfig;
      });

      portalHttpRequest.interceptors.response.use(
        (response): any => {
          set({ isLoading: false });

          return response.data;
        },
        (error): Promise<never> => {
          set({ isLoading: false });

          // redirects the user to sign-out when a 401 is received, indicating
          // their session has expired. suppressed for requests that may return
          // a 401 for reasons unrelated to session expiry (ex: fetching the
          // current user on page load when the user is not signed in).
          if (!error?.config?.suppressUnauthorizedRedirect) {
            handleUnauthorized(error);
          }

          return Promise.reject(error);
        },
      );

      return portalHttpRequest;
    },
  });
